import { CodeBlock } from '../../components/developers/CodeBlock';
import { MermaidDiagram } from '../../components/developers/MermaidDiagram';

export default function Authentication() {
  const authFlowDiagram = `sequenceDiagram
    participant Client
    participant Opair API
    participant Auth Service
    
    Client->>Opair API: POST /auth/token (credentials)
    Opair API->>Auth Service: Validate credentials
    Auth Service-->>Opair API: Token payload
    Opair API-->>Client: JWT access token
    Client->>Opair API: GET /api/v1/resource (Bearer token)
    Opair API->>Auth Service: Validate token
    Auth Service-->>Opair API: Valid
    Opair API-->>Client: Resource data`;

  return (
    <article className="prose prose-slate max-w-none">
      <h1>Authentication</h1>
      <p className="lead">
        Opair supports multiple authentication methods to secure your API requests.
      </p>

      <h2>Authentication Methods</h2>
      <p>Choose the method that best fits your use case:</p>

      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Use Case</th>
            <th>Security Level</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>API Key</td>
            <td>Server-to-server communication</td>
            <td>Medium</td>
          </tr>
          <tr>
            <td>JWT Token</td>
            <td>User sessions, web apps</td>
            <td>High</td>
          </tr>
          <tr>
            <td>OAuth 2.0</td>
            <td>Third-party integrations</td>
            <td>High</td>
          </tr>
        </tbody>
      </table>

      <h2>Authentication Flow</h2>
      <MermaidDiagram chart={authFlowDiagram} caption="JWT Authentication Flow" />

      <h2>API Key Authentication</h2>
      <p>Include your API key in the request header:</p>

      <CodeBlock
        language="bash"
        code={`curl -X GET "https://api.opair.example.com/v1/health" \\
  -H "X-API-Key: your-api-key-here"`}
      />

      <CodeBlock
        title="Python (requests)"
        language="python"
        code={`import requests

response = requests.get(
    "https://api.opair.example.com/v1/health",
    headers={"X-API-Key": "your-api-key-here"}
)
print(response.json())`}
      />

      <h2>Bearer Token Authentication</h2>
      <p>For JWT-based authentication, first obtain a token:</p>

      <CodeBlock
        title="Get Access Token"
        language="bash"
        code={`curl -X POST "https://api.opair.example.com/auth/token" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@example.com",
    "password": "your-password"
  }'`}
      />

      <p>Then use the token in subsequent requests:</p>

      <CodeBlock
        title="Authenticated Request"
        language="bash"
        code={`curl -X GET "https://api.opair.example.com/v1/policies" \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..."`}
      />

      <CodeBlock
        title="Python (requests)"
        language="python"
        code={`import requests

# Get access token
auth_response = requests.post(
    "https://api.opair.example.com/auth/token",
    json={"email": "user@example.com", "password": "your-password"}
)
token = auth_response.json()["accessToken"]

# Make authenticated request
response = requests.get(
    "https://api.opair.example.com/v1/policies",
    headers={"Authorization": f"Bearer {token}"}
)
print(response.json())`}
      />

      <h2>SDK Authentication</h2>
      <p>The SDK handles authentication automatically:</p>

      <CodeBlock
        title="sdk-auth.ts"
        language="typescript"
        code={`import { OpairClient } from '@opair/sdk';

// API Key authentication
const client = new OpairClient({
  apiKey: process.env.OPAIR_API_KEY!,
});

// Or JWT authentication
const clientWithJwt = new OpairClient({
  accessToken: 'your-jwt-token',
  refreshToken: 'your-refresh-token',
  onTokenRefresh: (newTokens) => {
    // Store new tokens
  },
});`}
      />

      <h2>Token Refresh</h2>
      <p>Access tokens expire after 1 hour. Use refresh tokens to obtain new access tokens:</p>

      <CodeBlock
        language="typescript"
        code={`const response = await client.auth.refresh({
  refreshToken: 'your-refresh-token',
});

console.log('New access token:', response.accessToken);`}
      />

      <div className="not-prose my-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <h3 className="text-lg font-semibold text-amber-900 mb-2">Security Best Practices</h3>
        <ul className="text-amber-800 space-y-1 text-sm">
          <li>Never expose API keys in client-side code</li>
          <li>Rotate API keys regularly</li>
          <li>Use environment variables for sensitive credentials</li>
          <li>Implement proper token storage (HttpOnly cookies for web apps)</li>
        </ul>
      </div>
    </article>
  );
}
